import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  inject,
  input,
  signal
} from '@angular/core';
import {
  ControlValueAccessor,
  UntypedFormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator
} from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { filter, first, map, skip, takeUntil, tap } from 'rxjs/operators';

import findIndex from 'lodash-es/findIndex';
import isArray from 'lodash-es/isArray';
import pick from 'lodash-es/pick';

import { FileResource } from '@app/shared/modules/resources/file.resource';
import { FileUploaderFiltersService } from '@app/shared/services/file-uploader-filters.service';
import { IFBApiErrorResponse } from '@interfaces';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { featureFlags } from '@utils/feature-flags';

type UiFile = {
  _index: number; // internal index
  id?: string;
  error?: string;
  inProgress?: boolean;
  cancelRequest?: () => void;
  href?: string;
  name?: string;
  size?: number;
};

@Component({
  selector: 'app-resume-uploader',
  templateUrl: './resume-uploader.component.html',
  styleUrls: ['./resume-uploader.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ResumeUploaderComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => ResumeUploaderComponent),
      multi: true
    }
  ],
  standalone: false
})
export class ResumeUploaderComponent implements ControlValueAccessor, Validator {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly fileResource = inject(FileResource);
  private readonly fileUploaderFilters = inject(FileUploaderFiltersService);
  private readonly http = inject(HttpClient);
  customClass = input('');
  showLabel = input(false);

  onTouched: () => void;
  onChange: (value: any) => void;

  readonly ERROR_HIDE_DELAY = 5000;

  private _index = 1; // counter
  // used to be able to set up validation, see validate method
  private control = new UntypedFormControl();
  private _files: UiFile[] = [];
  files$ = new Subject<UiFile[]>();
  files = toSignal(this.files$, {
    initialValue: []
  });
  isUploading$ = new Subject<boolean>();
  jobShareListFT = signal(featureFlags.isOn('Frontend.jobShareListRedesign'));

  constructor() {
    this.isUploading$.pipe(takeUntilDestroyed()).subscribe(val => {
      this.control.setErrors(val ? { uploading: true } : null);
    });

    this.files$
      .pipe(
        skip(1),
        filter(files => isArray(files)),
        map(files => files.filter(file => Boolean(file.id)).map(file => pick(file, 'id', 'name', 'href'))),
        takeUntilDestroyed()
      )
      .subscribe(files => {
        this.onChange(files);
      });
  }

  // Increases index on every call
  private get index() {
    return this._index++;
  }

  writeValue(value: UiFile[]): void {
    if (value) {
      this._files = value.map(file => {
        if (!file.name && file.href) {
          // Obtain file info from api
          this.http.get<UiFile>(file.href).subscribe(response => {
            response._index = file._index;
            const fileIndex = findIndex(this._files, { href: file.href });
            this._files.splice(fileIndex, 1, response);
            this.files$.next(this._files);
            this.cdr.detectChanges();
          });
        }

        file._index = this.index;
        return file;
      });

      this.files$.next(this._files);
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  validate(): ValidationErrors | null {
    return this.control.errors;
  }

  cancelFile(_index: number) {
    const file = this._files.find(f => f._index === _index);

    if (!file) {
      return;
    }

    if (file.cancelRequest) {
      file.cancelRequest();
    }

    this._files = this._files.filter(f => f._index !== _index);
    this.files$.next(this._files);
    this.onTouched();
  }

  removeFileByIndex(_index: number): void {
    const index = findIndex(this._files, { _index });
    this._files.splice(index, 1);
    this.files$.next(this._files);
    this.cdr.detectChanges();
  }

  addFile(file: UiFile) {
    this._files.push(file);
    this.files$.next(this._files);
  }

  setFilePropertyByIndex(_index: number, prop: string, value?: any) {
    const file = this._files.find(f => f._index === _index);
    if (!file) {
      return;
    }
    file[prop] = value;
    this.files$.next(this._files);
    this.cdr.detectChanges();
  }

  onFileUploadSuccess(_index: number, response: { id: string }) {
    this.setFilePropertyByIndex(_index, 'id', response.id);
    this.setFilePropertyByIndex(_index, 'inProgress', false);
    this.cdr.detectChanges();
  }

  onFileUploadError(_index: number, error: IFBApiErrorResponse) {
    if (error.data && error.data.code === '4220') {
      this.setFilePropertyByIndex(_index, 'error', 'common.error.file-malicious');
    } else {
      this.setFilePropertyByIndex(_index, 'error', 'common.error.failed-upload');
    }

    this.setFilePropertyByIndex(_index, 'inProgress', false);

    setTimeout(() => this.removeFileByIndex(_index), this.ERROR_HIDE_DELAY);
  }

  doUpload(file: File): Observable<null> {
    const uiFile: UiFile = {
      _index: this.index,
      name: file.name,
      size: file.size
    };

    this.addFile(uiFile);

    // Apply filters
    if (!this.fileUploaderFilters.resumeFilter(file)) {
      this.setFilePropertyByIndex(
        uiFile._index,
        'error',
        this.jobShareListFT() ? 'common.error.unsupported-format' : 'common.error.resume-file-expected'
      );
      setTimeout(() => this.removeFileByIndex(uiFile._index), this.ERROR_HIDE_DELAY);
      return of(null);
    } else if (!this.fileUploaderFilters.sizeFilter(file)) {
      this.setFilePropertyByIndex(
        uiFile._index,
        'error',
        this.jobShareListFT() ? 'common.error.file-is-too-large' : 'common.error.file-size'
      );
      setTimeout(() => this.removeFileByIndex(uiFile._index), this.ERROR_HIDE_DELAY);
      return of(null);
    }

    this.setFilePropertyByIndex(uiFile._index, 'inProgress', true);
    const cancelRequest = new Subject<null>();
    const requestFinished = new Subject<null>();

    // the reference to cancel request function
    uiFile.cancelRequest = () => {
      cancelRequest.next(null);
      cancelRequest.complete();
      requestFinished.next(null);
      requestFinished.complete();
      this.validate();
    };

    this.fileResource
      .upload(file, {
        'X-Filename': file.name,
        'Content-Type': 'application/json;charset=utf-8'
      })
      .pipe(
        takeUntil(cancelRequest),
        first(),
        tap(() => {
          requestFinished.next(null);
          requestFinished.complete();
        })
      )
      .subscribe(
        response => this.onFileUploadSuccess(uiFile._index, response),
        error => this.onFileUploadError(uiFile._index, error)
      );

    return requestFinished;
  }

  upload(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files);
    // The element's value is being reset to allow consequent selection of a same file.
    // This should be done synchronously inside the event handler so it won't cause
    // another change event trigger in IE.
    input.value = null;

    this.isUploading$.next(true);

    // to track when all the files have been uploaded
    forkJoin(files.map(file => this.doUpload(file))).subscribe({
      complete: () => this.isUploading$.next(false)
    });

    this.onTouched();
  }
}
